<template>
    <div class="layout-status-alerts-settings w-full">
        <RedesignBaseForm
            ref="statusAlertsForm"
            class="space-y-6"
        >
            <!-- Header Section -->
            <div class="w-full">
                <div class="uppercase font-semibold mb-2">
                    {{ $t('layout.config.statusAlerts.title') }}
                </div>
                <div class="text-sm">
                    {{ $t('layout.config.statusAlerts.description') }}
                </div>
            </div>

            <!-- Global Colors Section -->
            <div class="global-colors-section border border-ui-lines rounded-lg p-4 space-y-4">
                <div class="uppercase font-semibold text-sm">
                    {{ $t('layout.config.statusAlerts.alertColors') }}
                </div>

                <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <!-- Warning Color -->
                    <div class="config-field">
                        <label class="text-sm font-medium mb-2 block">
                            {{ $t('layout.config.loginStatusWarningColor') }}
                        </label>
                        <RedesignWidgetColor
                            :value="globalWarningColor"
                            size="medium"
                            @input="updateGlobalColor('LoginStatusWarningColor', $event)"
                        />
                    </div>

                    <!-- Limit Color -->
                    <div class="config-field">
                        <label class="text-sm font-medium mb-2 block">
                            {{ $t('layout.config.loginStatusLimitColor') }}
                        </label>
                        <RedesignWidgetColor
                            :value="globalLimitColor"
                            size="medium"
                            @input="updateGlobalColor('LoginStatusLimitColor', $event)"
                        />
                    </div>
                </div>
            </div>

            <!-- Quick Apply Sounds Section -->
            <div class="quick-apply-section border border-ui-lines rounded-lg p-4 space-y-4">
                <div class="flex items-center justify-between">
                    <div class="uppercase font-semibold text-sm">
                        {{ $t('layout.config.statusAlerts.quickApplySounds') }}
                    </div>
                    <RedesignBaseButton
                        type="soft"
                        color="primary"
                        size="small"
                        icon="Copy"
                        @click="applySelectedSoundsToAll"
                    >
                        {{ $t('layout.config.statusAlerts.applyToAll') }}
                    </RedesignBaseButton>
                </div>

                <div class="text-xs text-gray-500 mb-3">
                    {{ $t('layout.config.statusAlerts.quickApplyDescription') }}
                </div>

                <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <!-- Warning Sound Selector -->
                    <div class="config-field">
                        <label class="text-sm font-medium mb-2 block">
                            {{ $t('layout.config.statusAlerts.warningSound') }}
                        </label>
                        <SoundSelect
                            v-model="selectedWarningSound"
                            :options="warningSoundList"
                            :placeholder="$t('layout.config.statusAlerts.selectSound')"
                        />
                    </div>

                    <!-- Limit Sound Selector -->
                    <div class="config-field">
                        <label class="text-sm font-medium mb-2 block">
                            {{ $t('layout.config.statusAlerts.limitSound') }}
                        </label>
                        <SoundSelect
                            v-model="selectedLimitSound"
                            :options="errorSoundList"
                            :placeholder="$t('layout.config.statusAlerts.selectSound')"
                        />
                    </div>
                </div>
            </div>

            <!-- Status Accordion List -->
            <div class="status-alerts-list">
                <BaseCollapse v-model="expandedStatuses">
                    <div
                        v-for="status in availableStatuses"
                        :key="status.value"
                        class="status-alert-item border border-ui-lines rounded-lg mb-2 overflow-hidden"
                        :class="{ 'is-expanded': isExpanded(status.value) }"
                    >
                        <BaseCollapseItem
                            :name="String(status.value)"
                            class="status-collapse-item"
                        >
                            <!-- Custom Title Slot -->
                            <template #title>
                                <div class="status-header flex items-center gap-3 w-full">
                                    <!-- Status Icon with Color -->
                                    <WidgetStatusIcon
                                        :icon="status.icon"
                                        :color="status.color"
                                        :size="18"
                                        :offset="4"
                                        class="flex-shrink-0 rounded"
                                    />

                                    <span class="font-medium text-base flex-grow">
                                        {{ status.text }}
                                    </span>

                                    <!-- Configuration Summary Badge -->
                                    <div
                                        v-if="hasStatusConfig(status.value)"
                                        class="config-badge text-xs px-2 py-0.5 rounded-full bg-primary-actions/10 text-primary-actions"
                                    >
                                        {{ $t('layout.config.statusAlerts.configured') }}
                                    </div>
                                </div>
                            </template>
                            <div class="status-config-panel p-4">
                                <!-- Disable Alerts Toggle -->
                                <div class="disable-alerts-toggle flex items-center gap-3 mb-4 pb-4 border-b border-ui-lines">
                                    <RedesignSwitch
                                        :value="getStatusConfig(status.value, 'disableStatusAlerts')"
                                        @change="onToggleDisableAlerts(status.value, $event)"
                                    />
                                    <label class="text-sm font-medium">
                                        {{ $t('layout.config.statusAlerts.disableStatusAlerts') }}
                                    </label>
                                </div>

                                <div
                                    class="grid grid-cols-1 lg:grid-cols-2 gap-6 transition-opacity duration-200"
                                    :class="{ 'opacity-50 pointer-events-none': !!getStatusConfig(status.value, 'disableStatusAlerts') }"
                                >
                                    <!-- Warning Configuration (Left Side) -->
                                    <div
                                        class="warning-config p-4 rounded-lg border-2 transition-colors duration-200"
                                        :style="getConfigCardStyle('warning')"
                                    >
                                        <div class="config-section-title text-sm font-semibold mb-4 flex items-center gap-2">
                                            <div
                                                class="w-3 h-3 rounded-full"
                                                :style="{ backgroundColor: globalWarningColor }"
                                            />
                                            {{ $t('layout.config.statusAlerts.warningSettings') }}
                                        </div>

                                        <!-- Warning Time -->
                                        <div class="config-field mb-4">
                                            <label class="text-sm font-medium mb-2 block">
                                                {{ $t('layout.config.statusAlerts.warningTime') }}
                                                <span class="text-xs text-gray-500 ml-1">({{ $t('layout.config.statusAlerts.seconds') }})</span>
                                            </label>
                                            <RedesignInputNumber
                                                :value="getStatusConfig(status.value, 'warning')"
                                                :min-value="0"
                                                :step="1"
                                                :precision="0"
                                                :placeholder="$t('layout.config.statusAlerts.enterSeconds')"
                                                @change="updateStatusConfig(status.value, 'warning', $event)"
                                            />
                                        </div>

                                        <!-- Warning Sound -->
                                        <div class="config-field">
                                            <label class="text-sm font-medium mb-2 block">
                                                {{ $t('layout.config.statusAlerts.warningSound') }}
                                            </label>
                                            <SoundSelect
                                                :value="getStatusConfig(status.value, 'warningSound')"
                                                :options="warningSoundList"
                                                :placeholder="$t('layout.config.statusAlerts.selectSound')"
                                                @input="updateStatusConfig(status.value, 'warningSound', $event)"
                                            />
                                        </div>
                                    </div>

                                    <!-- Limit Configuration (Right Side) -->
                                    <div
                                        class="limit-config p-4 rounded-lg border-2 transition-colors duration-200"
                                        :style="getConfigCardStyle('limit')"
                                    >
                                        <div class="config-section-title text-sm font-semibold mb-4 flex items-center gap-2">
                                            <div
                                                class="w-3 h-3 rounded-full"
                                                :style="{ backgroundColor: globalLimitColor }"
                                            />
                                            {{ $t('layout.config.statusAlerts.limitSettings') }}
                                        </div>

                                        <!-- Limit Time -->
                                        <div class="config-field mb-4">
                                            <label class="text-sm font-medium mb-2 block">
                                                {{ $t('layout.config.statusAlerts.limitTime') }}
                                                <span class="text-xs text-gray-500 ml-1">({{ $t('layout.config.statusAlerts.seconds') }})</span>
                                            </label>
                                            <RedesignInputNumber
                                                :value="getStatusConfig(status.value, 'limit')"
                                                :min-value="0"
                                                :step="1"
                                                :precision="0"
                                                :placeholder="$t('layout.config.statusAlerts.enterSeconds')"
                                                @change="updateStatusConfig(status.value, 'limit', $event)"
                                            />
                                        </div>

                                        <!-- Limit Sound -->
                                        <div class="config-field">
                                            <label class="text-sm font-medium mb-2 block">
                                                {{ $t('layout.config.statusAlerts.limitSound') }}
                                            </label>
                                            <SoundSelect
                                                :value="getStatusConfig(status.value, 'limitSound')"
                                                :options="errorSoundList"
                                                :placeholder="$t('layout.config.statusAlerts.selectSound')"
                                                @input="updateStatusConfig(status.value, 'limitSound', $event)"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </BaseCollapseItem>
                    </div>
                </BaseCollapse>
            </div>
        </RedesignBaseForm>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useVModel } from '@vueuse/core'
import RedesignBaseForm from '@/components/RedesignBaseComponents/Form/RedesignBaseForm.vue'
import RedesignWidgetColor from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetColor.vue'
import WidgetStatusIcon from '@/components/RedesignComponents/Cards/WidgetStatusIcon.vue'
import BaseCollapse from '@/components/RedesignBaseComponents/Collapse/BaseCollapse.vue'
import BaseCollapseItem from '@/components/RedesignBaseComponents/Collapse/BaseCollapseItem.vue'
import RedesignInputNumber from '@/components/RedesignBaseComponents/Inputs/RedesignInputNumber.vue'
import SoundSelect from '@/components/Common/SoundSelect.vue'
import RedesignBaseButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseButton.vue'
import RedesignSwitch from '@/components/RedesignBaseComponents/Inputs/RedesignSwitch.vue'
import { ILayoutItem, IAccountStatusItem, ILayoutParametersList } from '@/types/dashboard'
import {
    entitiesData,
    errorSoundList,
    selectedAccountBreaks,
    warningSoundList
} from '@/composables/globalData/useEntitiesData'
import { getLayoutParameterByName, getDefaultStatusConfig, StatusAlertConfig, StatusAlertsConfigMap } from '@/composables/globalData/useLayouts'
import { LAYOUT_PARAMETERS_DEFAULTS } from '@/enum/default-layout-config'
import { layoutDefaultColors } from '@/enum/colors'
import statusTypes, { isStatusIsCallStatus } from '@/enum/redesignStatusTypes'


const props = defineProps<{
    value: ILayoutItem
}>()

const emit = defineEmits<{
    (e: 'input', value: ILayoutItem): void
    (e: 'update:value', value: ILayoutItem): void
}>()

const layoutLocalModel = useVModel(props, 'value', emit, { eventName: 'input' })

/**
 * Local state for quick apply sounds (not stored in layout)
 */
const selectedWarningSound = ref<string>('')
const selectedLimitSound = ref<string>('')

/**
 * Track which statuses are expanded (array of status ID strings for BaseCollapse)
 */
const expandedStatuses = ref<string[]>([])

/**
 * Check if a status is expanded
 */
function isExpanded (statusId: number): boolean {
    return expandedStatuses.value.includes(String(statusId))
}

/**
 * Check if a status has any custom configuration set
 * A status is "configured" if the user has interacted with it
 * (i.e., a config object exists for this status in the configuration map)
 */
function hasStatusConfig (statusId: number): boolean {
    const config = statusAlertsConfig.value[statusId]
    // If config object exists, user has configured this status
    return !!config
}

/**
 * Layout parameters list computed property
 */
const layoutParams = computed(() => layoutLocalModel.value?.LayoutParametersList || [])

/**
 * Get global warning color from layout parameters
 */
const globalWarningColor = computed(() => {
    const param = getLayoutParameterByName(layoutParams.value, 'LoginStatusWarningColor')
    return param?.Value || layoutDefaultColors.loginStatusWarning
})

/**
 * Get global limit color from layout parameters
 */
const globalLimitColor = computed(() => {
    const param = getLayoutParameterByName(layoutParams.value, 'LoginStatusLimitColor')
    return param?.Value || layoutDefaultColors.loginStatusLimit
})

/**
 * Update global color parameter
 */
function updateGlobalColor (parameterName: string, value: string) {
    updateLayoutParameter(parameterName, value)
}

/**
 * Update a layout parameter value
 */
function updateLayoutParameter (parameterName: string, value: string) {
    const params = [ ...(layoutLocalModel.value?.LayoutParametersList || []) ]
    const paramIndex = params.findIndex(p => p.LayoutParameterName === parameterName)

    if (paramIndex !== -1) {
        params[paramIndex] = {
            ...params[paramIndex],
            Value: value
        }
    } else if (LAYOUT_PARAMETERS_DEFAULTS[parameterName]) {
        params.push({
            ...LAYOUT_PARAMETERS_DEFAULTS[parameterName],
            Value: value
        })
    }

    layoutLocalModel.value = {
        ...layoutLocalModel.value,
        LayoutParametersList: params
    }
}

/**
 * Apply selected sounds to all statuses
 * Uses the local selectedWarningSound and selectedLimitSound refs
 */
function applySelectedSoundsToAll () {
    if (!selectedWarningSound.value && !selectedLimitSound.value) {
        return
    }

    const currentConfig = { ...statusAlertsConfig.value }

    availableStatuses.value.forEach(status => {
        if (!currentConfig[status.value]) {
            currentConfig[status.value] = getDefaultStatusConfig()
        }

        // Apply selected sounds to each status
        if (selectedWarningSound.value) {
            currentConfig[status.value].warningSound = selectedWarningSound.value
        }
        if (selectedLimitSound.value) {
            currentConfig[status.value].limitSound = selectedLimitSound.value
        }
    })

    // Update the layout parameter
    const params = [ ...(layoutLocalModel.value?.LayoutParametersList || []) ]
    const configParamIndex = params.findIndex(p => p.LayoutParameterName === 'LoginStatusesConfiguration')

    const newConfigValue = JSON.stringify(currentConfig)

    if (configParamIndex !== -1) {
        params[configParamIndex] = {
            ...params[configParamIndex],
            ValueText: newConfigValue
        }
    } else {
        params.push({
            ...LAYOUT_PARAMETERS_DEFAULTS['LoginStatusesConfiguration'],
            ValueText: newConfigValue
        })
    }

    layoutLocalModel.value = {
        ...layoutLocalModel.value,
        LayoutParametersList: params
    }

    // Reset the selection after applying
    selectedWarningSound.value = ''
    selectedLimitSound.value = ''
}

function getConfigCardStyle (type: 'warning' | 'limit'): Record<string, string> {
    const color = type === 'warning' ? globalWarningColor.value : globalLimitColor.value

    return {
        backgroundColor: `${color}10`,
        borderColor: `${color}40`
    }
}

const availableStatuses = computed<IAccountStatusItem[]>(() => {
    const accountBreaks = selectedAccountBreaks.value
    const defaultAccountBreaks = entitiesData?.value?.DefaultAccountBreak ?? []
    const defaultStatuses = statusTypes.value

    // Use accountBreaks if available, otherwise fallback to defaultAccountBreaks
    const statusesToUse = accountBreaks?.length ? accountBreaks : defaultAccountBreaks

    if (statusesToUse?.length) {
        return statusesToUse.map(status => {
            const defaultStatus = defaultStatuses[status.StatusID]
            return {
                ...status,
                value: status.StatusID,
                icon: defaultStatus?.icon || 'Settings',
                color: status.ColorCode || defaultStatus?.color,
                text: status.Name || defaultStatus?.text || `Status ${status.StatusID}`,
                limit: status.AlertSecLimit || 1800,
                warning: status.WarningSecLimit || 60
            }
        })
    }

    // Return default status types if no breaks available at all
    // Filter out call statuses (IN_CALL, HOLD, CALLING) as alerts should not apply to them
    return Object.values(defaultStatuses).filter(status => !isStatusIsCallStatus(status.value))
})

/**
 * Parse the current status alerts configuration from layout parameters
 */
const statusAlertsConfig = computed<StatusAlertsConfigMap>(() => {
    const configParam = getLayoutParameterByName(layoutParams.value, 'LoginStatusesConfiguration')

    if (configParam?.ValueText) {
        try {
            return JSON.parse(configParam.ValueText)
        } catch {
            return {}
        }
    }

    return {}
})

/**
 * Get status config value for display in UI
 * Fallback chain: per-status config → defaults
 */
function getStatusConfig (statusId: number, key: keyof StatusAlertConfig): string | number | boolean {
    const config = statusAlertsConfig.value[statusId]

    // If per-status has a value, use it
    if (config && config[key]) {
        return config[key]
    }

    // Use defaults
    return getDefaultStatusConfig()[key]
}

function onToggleDisableAlerts (statusId: number, value: boolean) {
    updateStatusConfig(statusId, 'disableStatusAlerts', value)
}

/**
 * Update a specific configuration value for a status
 */
function updateStatusConfig (statusId: number, key: keyof StatusAlertConfig, value: string | number) {
    const currentConfig = { ...statusAlertsConfig.value }

    // Only create object if it doesn't exist, don't pre-fill with defaults
    if (!currentConfig[statusId]) {
        currentConfig[statusId] = getDefaultStatusConfig()
    }

    // Only update the specific field that was changed
    currentConfig[statusId] = {
        ...currentConfig[statusId],
        [key]: value
    }

    // Update the layout parameter
    const params = [ ...(layoutLocalModel.value?.LayoutParametersList || []) ]
    const configParamIndex = params.findIndex(p => p.LayoutParameterName === 'LoginStatusesConfiguration')

    const newConfigValue = JSON.stringify(currentConfig)

    if (configParamIndex !== -1) {
        params[configParamIndex] = {
            ...params[configParamIndex],
            ValueText: newConfigValue
        }
    } else {
        // Create the parameter if it doesn't exist
        params.push({
            ...LAYOUT_PARAMETERS_DEFAULTS['LoginStatusesConfiguration'],
            ValueText: newConfigValue
        })
    }

    layoutLocalModel.value = {
        ...layoutLocalModel.value,
        LayoutParametersList: params
    }
}
</script>

<style lang="scss">
.layout-status-alerts-settings {
    .global-colors-section,
    .quick-apply-section {
        background-color: var(--secondaryBgColor-color);
    }

    .status-collapse-item {
        .el-collapse-item__header {
          @apply px-2 py-6;

            .el-collapse-item__arrow {
                @apply mx-2;
                color: var(--gray-500);
                transition: transform 0.3s ease;
            }
        }
    }

    .status-alert-item.is-expanded {
        .el-collapse-item__header {
            .el-collapse-item__arrow {
                color: var(--primary-actions);
            }
        }
    }

    .config-badge {
        font-weight: 500;
    }

    .config-field {
        .input-number {
            max-width: 230px;
        }
    }

    .warning-config,
    .limit-config {
        min-height: 140px;

        .config-section-title {
            color: inherit;
            opacity: 0.9;
        }
    }
}
</style>
